/**
 * Debug tracing for Users & Roles list, assign, and sign-in link flows.
 * Filter the browser console with: [UsersRolesDebug]
 *
 * Enable in production builds: localStorage.setItem('harry.debug.usersRoles', 'true')
 */
import { getCorrelationId } from '@/lib/logging/correlation-id';

export function isUsersRolesDebugEnabled(): boolean {
  if (typeof process !== 'undefined' && process.env?.HARRY_DEBUG_USERS_ROLES === 'true') {
    return true;
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    return true;
  }
  if (typeof window !== 'undefined') {
    try {
      return window.localStorage.getItem('harry.debug.usersRoles') === 'true';
    } catch {
      return false;
    }
  }
  return process.env.NODE_ENV !== 'production';
}

export function createUsersRolesTrace(operation: string): string {
  const traceId = `${operation}-${Date.now().toString(36)}`;
  logUsersRolesDebug('trace', `start ${operation}`, { traceId });
  return traceId;
}

export function logUsersRolesDebug(
  scope: string,
  message: string,
  data?: Record<string, unknown>
): void {
  if (!isUsersRolesDebugEnabled()) {
    return;
  }

  const label = `[UsersRolesDebug] ${scope}: ${message}`;
  const correlationId = typeof window !== 'undefined' ? getCorrelationId() : undefined;
  const payload = data
    ? { ...data, ...(correlationId ? { correlationId } : {}) }
    : correlationId
      ? { correlationId }
      : undefined;

  if (typeof window !== 'undefined') {
    void import('@/lib/logging/logger').then(({ logger }) => {
      logger.debug(label, payload ?? data ?? {});
    });
  }
}

export async function parseFetchErrorBody(
  response: Response
): Promise<{ error?: string; message?: string; raw?: string }> {
  const raw = await response.text();
  try {
    const json = JSON.parse(raw) as { error?: string; message?: string };
    return { error: json.error, message: json.message, raw };
  } catch {
    return { raw };
  }
}

export function logFetchResult(
  traceId: string,
  label: string,
  response: Response,
  parsed?: { error?: string; message?: string; raw?: string },
  extra?: Record<string, unknown>
): void {
  logUsersRolesDebug(label, `${traceId} HTTP ${response.status} ${response.statusText}`, {
    ok: response.ok,
    status: response.status,
    url: response.url,
    error: parsed?.error,
    message: parsed?.message,
    bodyPreview: parsed?.raw?.slice(0, 500),
    ...extra,
  });
}
